import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { ActivityIcon, ChartIcon, CheckCircleIcon, ExpandIcon, RefreshIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useMetricsApi, useSitesApi } from '@app/hooks/useApis';
import type { PingMeshCandidate, PingMeshCell, PingMeshDistributionschema, PingMeshHistoryschema, PingMeshLatestResultschema, PingMeshMetricRangeschema, PingMeshMetricValueschema, PingMeshQueryResponse, Site } from '@app/types/sdk';
import { collectCursorPages } from '@app/utils/collectCursorPages';

type HistoryLookback = '1h' | '6h' | '12h' | '24h';
type AutoRefreshInterval = 'off' | '60s';
type PresentationSite = { id: string; name: string; kind: string };
type RTTValue = { stat: string; value: number };
type RTTPercentiles = { p50?: number; p90?: number; p95?: number; p99?: number };
type ProbePoint = {
  observedAt: Date;
  packetLoss?: number;
  min?: number;
  avg?: number;
  max?: number;
  deviation?: number;
  maxFlags: string[];
};
type CandidateResult = {
  candidate: PingMeshCandidate;
  latest: PingMeshLatestResultschema;
  status: string;
  freshness: string;
  freshnessAgeSeconds: number;
  packetLoss?: number;
  averageRtt?: number;
  rtt: RTTValue[];
  candidateCount?: number;
};
type Viewport = { start: number; end: number };
type TimelineRange = { start: number; end: number };
type MatrixHover = { source: PresentationSite; destination: PresentationSite; result?: CandidateResult; unavailable?: boolean; style: CSSProperties };

const HISTORY_LOOKBACKS: HistoryLookback[] = ['1h', '6h', '12h', '24h'];
const AUTO_REFRESH_INTERVALS: { value: AutoRefreshInterval; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: '60s', label: '60s' }
];
const PACKET_LOSS_METRIC = 'ping_mesh_packet_loss_ratio';
const RTT_METRIC = 'ping_mesh_rtt_seconds';
const RTT_HISTOGRAM_METRIC = 'ping_mesh_packet_rtt_seconds';
const STATUS_PRIORITY: Record<string, number> = { healthy: 0, degraded: 1, critical: 2, down: 3 };

function formatMilliseconds(value: number): string {
  return `${(value * 1000).toFixed(value < 0.01 ? 2 : 1)} ms`;
}

function lossPercent(value: number): string {
  return `${(value * 100).toFixed(value > 0 && value < 0.01 ? 1 : 0)}%`;
}

function displayStatus(value: string): string {
  return value ? `${value[0].toUpperCase()}${value.slice(1)}` : 'Unknown';
}

function formatTimestamp(value: Date | string | undefined): string {
  if (!value) return 'Unknown';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', month: 'short', day: 'numeric' });
}

function formatProbeTime(value: Date): string {
  const date = value.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = value.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
  return `${date} ${time}`;
}

function formatAge(seconds: number): string {
  const age = Math.max(0, Math.round(seconds));
  if (age < 60) return `${age}s ago`;
  const minutes = Math.floor(age / 60);
  if (minutes < 60) return `${minutes}m${age % 60 ? ` ${age % 60}s` : ''} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h${minutes % 60 ? ` ${minutes % 60}m` : ''} ago`;
}

function siteKind(site: Site): string {
  return String(site.siteType ?? '').toLowerCase().includes('cloud') ? 'Cloud' : 'On-prem';
}

function hasPingEndpoint(site: Site): boolean {
  return typeof site.pingEndpointIp === 'string' && site.pingEndpointIp.trim() !== '';
}

function buildSites(sites: Site[]): PresentationSite[] {
  return sites
    .filter(hasPingEndpoint)
    .flatMap((site) => site.id ? [{ id: site.id, name: site.name, kind: siteKind(site) }] : [])
    .sort((left, right) => left.name.localeCompare(right.name));
}

function sampleDate(value: Date | string): Date | undefined {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function latestMetricValue(metrics: PingMeshMetricValueschema[], name: string, stat?: string): number | undefined {
  return metrics.find((metric) => metric.name === name && (stat === undefined || metric.labels.stat === stat))?.value;
}

function latestRTTValues(latest: PingMeshLatestResultschema): RTTValue[] {
  return latest.metrics
    .filter((metric) => metric.name === RTT_METRIC)
    .map((metric) => ({ stat: metric.labels.stat || 'value', value: metric.value }))
    .sort((left, right) => left.stat.localeCompare(right.stat));
}

function candidateResult(candidate: PingMeshCandidate): CandidateResult | undefined {
  if (!candidate.latest) return undefined;
  const { latest } = candidate;
  const loss = latestMetricValue(latest.metrics, PACKET_LOSS_METRIC);
  const packetLoss = loss !== undefined && loss >= 0 && loss <= 1 ? loss : undefined;
  const rtt = latestRTTValues(latest);
  return {
    candidate,
    latest,
    status: latest.status,
    freshness: latest.freshness.state,
    freshnessAgeSeconds: latest.freshness.ageSeconds,
    packetLoss,
    averageRtt: rtt.find((value) => value.stat === 'avg')?.value,
    rtt
  };
}

export function primaryResult(cell: PingMeshCell): CandidateResult | undefined {
  const results = cell.candidates.map(candidateResult).filter((result): result is CandidateResult => Boolean(result));
  const result = results.sort((left, right) => {
    // Retired endpoint pairs remain in Prometheus for the query lookback.
    // A stale failure must not mask a currently reporting path.
    if (left.freshness !== right.freshness) return left.freshness === 'fresh' ? -1 : 1;
    const statusDifference = (STATUS_PRIORITY[right.status] ?? -1) - (STATUS_PRIORITY[left.status] ?? -1);
    if (statusDifference !== 0) return statusDifference;
    return right.freshnessAgeSeconds - left.freshnessAgeSeconds;
  })[0];
  return result ? { ...result, candidateCount: results.length } : undefined;
}

function dateKey(value: Date): string {
  return String(value.getTime());
}

function nearestProbe(points: ProbePoint[], target: number): ProbePoint | undefined {
  if (points.length === 0) return undefined;
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (points[middle].observedAt.getTime() < target) low = middle + 1;
    else high = middle;
  }
  const next = points[low];
  const previous = points[low - 1];
  return previous && Math.abs(previous.observedAt.getTime() - target) <= Math.abs(next.observedAt.getTime() - target) ? previous : next;
}

// The BFF has already collapsed retained Prometheus scrapes. This only joins
// generic metric families that share a probe completion timestamp into one UI
// point; it never performs scrape-level deduplication in the browser.
export function buildPingMeshProbeHistory(candidate: PingMeshCandidate): ProbePoint[] {
  const byTime = new Map<string, ProbePoint>();
  const pointAt = (value: Date | string): ProbePoint | undefined => {
    const observedAt = sampleDate(value);
    if (!observedAt) return undefined;
    const key = dateKey(observedAt);
    const existing = byTime.get(key);
    if (existing) return existing;
    const point: ProbePoint = { observedAt, maxFlags: [] };
    byTime.set(key, point);
    return point;
  };

  for (const metric of candidate.history?.series ?? []) {
    for (const sample of metric.samples) {
      const point = pointAt(sample.observedAt);
      if (!point) continue;
      if (metric.name === PACKET_LOSS_METRIC) {
        if (sample.value >= 0 && sample.value <= 1) point.packetLoss = sample.value;
        continue;
      }
      if (metric.name !== RTT_METRIC) continue;
      switch (sample.labels.stat || 'value') {
        case 'min': point.min = sample.value; break;
        case 'avg': point.avg = sample.value; break;
        case 'max':
          point.max = sample.value;
          point.maxFlags.push(...(sample.flags ?? []).map((flag) => flag.name));
          break;
        case 'mdev':
        case 'stddev': point.deviation = sample.value; break;
        default: break;
      }
    }
  }
  return [...byTime.values()].sort((left, right) => left.observedAt.getTime() - right.observedAt.getTime());
}

export default function PingMeshPage() {
  const orgId = useActiveOrgId();
  const metricsApi = useMetricsApi();
  const sitesApi = useSitesApi();
  // Start with enough retained probes to make the first detail view useful;
  // the user can still narrow it to 1h or 6h from the window control.
  const [historyLookback, setHistoryLookback] = useState<HistoryLookback>('12h');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshInterval>('off');
  const [selectedPair, setSelectedPair] = useState<string | null>(null);
  const [matrixHover, setMatrixHover] = useState<MatrixHover | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [destinationFilter, setDestinationFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [freshnessFilter, setFreshnessFilter] = useState<string>('');
  const [noDataFilter, setNoDataFilter] = useState(false);
  const reconciliationRefreshRef = useRef<string | null>(null);
  const selectedSelector = useMemo(() => {
    const [sourceSiteId, destinationSiteId] = selectedPair?.split(':') ?? [];
    return sourceSiteId && destinationSiteId ? { sourceSiteId, destinationSiteId } : undefined;
  }, [selectedPair]);

  const latestFetcher = useMemo(
    () => orgId ? () => metricsApi.orgsOrgIdMetricsPingMeshPost(
      { orgId, pingMeshQueryRequest: { query: { mode: 'latest' } } },
      { cache: 'no-store' }
    ) : null,
    [metricsApi, orgId, refreshVersion]
  );
  const historyFetcher = useMemo(
    () => orgId && selectedSelector ? () => metricsApi.orgsOrgIdMetricsPingMeshPost(
      { orgId, pingMeshQueryRequest: { selector: selectedSelector, query: { mode: 'range', lookback: historyLookback } } },
      { cache: 'no-store' }
    ) : null,
    [historyLookback, metricsApi, orgId, selectedSelector]
  );
  const sitesFetcher = useMemo(
    () => orgId ? () => collectCursorPages<Site>((after) => sitesApi.orgsOrgIdSitesGet({ orgId, limit: 200, after }, { cache: 'no-store' })) : null,
    [orgId, sitesApi]
  );

  const latestQuery = useApiQuery<PingMeshQueryResponse>(latestFetcher, [latestFetcher]);
  const historyQuery = useApiQuery<PingMeshQueryResponse>(historyFetcher, [historyFetcher]);
  const sitesQuery = useApiQuery(sitesFetcher, [sitesFetcher]);
  const cells = latestQuery.data?.cells ?? [];
  const sites = useMemo(() => buildSites(sitesQuery.data?.items ?? []), [sitesQuery.data?.items]);
  const cellByPair = useMemo(() => new Map<string, PingMeshCell>(cells.map((cell) => [`${cell.sourceSiteId}:${cell.destinationSiteId}`, cell])), [cells]);
  const selectedLatestCell = selectedPair ? cellByPair.get(selectedPair) : undefined;
  const selectedLatestResult = selectedLatestCell ? primaryResult(selectedLatestCell) : undefined;
  const selectedHistoryCell = selectedSelector
    ? historyQuery.data?.cells.find((cell) => cell.sourceSiteId === selectedSelector.sourceSiteId && cell.destinationSiteId === selectedSelector.destinationSiteId)
    : undefined;
  const selectedHistoryCandidate = selectedHistoryCell?.candidates.find((candidate) => candidate.sourceDeviceId === selectedLatestResult?.candidate.sourceDeviceId
    && candidate.sourceIp === selectedLatestResult.candidate.sourceIp
    && candidate.destinationIp === selectedLatestResult.candidate.destinationIp);
  const newestHistoryProbeAt = useMemo(() => {
    const samples = selectedHistoryCandidate?.history?.series.find((series) => series.name === PACKET_LOSS_METRIC)?.samples ?? [];
    return samples.reduce<number | undefined>((newest, sample) => {
      const observedAt = sampleDate(sample.observedAt)?.getTime();
      return observedAt !== undefined && (newest === undefined || observedAt > newest) ? observedAt : newest;
    }, undefined);
  }, [selectedHistoryCandidate]);
  const currentProbeAt = selectedLatestResult ? sampleDate(selectedLatestResult.latest.observedAt)?.getTime() : undefined;
  const displayedResults = cells.map(primaryResult).filter((result): result is CandidateResult => Boolean(result));
  const healthyCells = displayedResults.filter((result) => result.status === 'healthy').length;
  const degradedCells = displayedResults.filter((result) => result.status === 'degraded').length;
  const criticalCells = displayedResults.filter((result) => result.status === 'critical').length;
  const downCells = displayedResults.filter((result) => result.status === 'down').length;
  const staleCells = displayedResults.filter((result) => result.freshness === 'stale').length;
  const totalPaths = sites.length * Math.max(0, sites.length - 1);
  const noDataPaths = Math.max(0, totalPaths - displayedResults.length);
  const visibleSources = sourceFilter.length > 0 ? sites.filter((site) => sourceFilter.includes(site.id)) : sites;
  const visibleDestinations = destinationFilter.length > 0 ? sites.filter((site) => destinationFilter.includes(site.id)) : sites;
  const hasKpiFilter = Boolean(statusFilter || freshnessFilter || noDataFilter);
  const hasSiteFilter = sourceFilter.length > 0 || destinationFilter.length > 0;
  const isLoading = latestQuery.loading || sitesQuery.loading;
  const error = latestQuery.error ?? sitesQuery.error;
  const refresh = () => setRefreshVersion((value) => value + 1);
  useEffect(() => {
    if (!selectedPair || newestHistoryProbeAt === undefined || currentProbeAt === undefined || newestHistoryProbeAt <= currentProbeAt) {
      reconciliationRefreshRef.current = null;
      return;
    }
    if (latestQuery.loading) return;
    const key = `${selectedPair}:${newestHistoryProbeAt}`;
    if (reconciliationRefreshRef.current === key) return;
    reconciliationRefreshRef.current = key;
    setRefreshVersion((value) => value + 1);
  }, [currentProbeAt, latestQuery.loading, newestHistoryProbeAt, selectedPair]);
  useEffect(() => {
    if (autoRefresh !== '60s') return undefined;
    const interval = window.setInterval(() => setRefreshVersion((value) => value + 1), 60_000);
    return () => window.clearInterval(interval);
  }, [autoRefresh]);
  const matchesMatrixFilters = (cell?: PingMeshCell): boolean => {
    if (!cell || cell.candidates.length === 0) return noDataFilter || (!statusFilter && !freshnessFilter);
    if (noDataFilter) return false;
    const result = primaryResult(cell);
    return Boolean(result && (!statusFilter || result.status === statusFilter) && (!freshnessFilter || result.freshness === freshnessFilter));
  };
  const clearKpiFilters = () => {
    setStatusFilter('');
    setFreshnessFilter('');
    setNoDataFilter(false);
  };
  const clearSiteFilters = () => {
    setSourceFilter([]);
    setDestinationFilter([]);
  };
  const filterByStatus = (status: string) => {
    setStatusFilter((current) => current === status ? '' : status);
    setFreshnessFilter('');
    setNoDataFilter(false);
  };
  const filterByFreshness = (freshness: string) => {
    setFreshnessFilter((current) => current === freshness ? '' : freshness);
    setStatusFilter('');
    setNoDataFilter(false);
  };
  const filterByNoData = () => {
    setNoDataFilter((current) => !current);
    setStatusFilter('');
    setFreshnessFilter('');
  };
  const showMatrixHover = (event: PointerEvent<HTMLElement>, source: PresentationSite, destination: PresentationSite, result?: CandidateResult, unavailable = false) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 230;
    const height = result ? 132 : 60;
    const center = rect.left + rect.width / 2;
    const nearLeft = center < width / 2 + 16;
    const nearRight = center > window.innerWidth - width / 2 - 16;
    const below = rect.bottom + height + 8 <= window.innerHeight;
    setMatrixHover({
      source,
      destination,
      result,
      unavailable,
      style: {
        left: nearLeft ? rect.left : nearRight ? rect.right : center,
        top: below ? rect.bottom + 8 : rect.top - 8,
        transform: `translate(${nearLeft ? '0' : nearRight ? '-100%' : '-50%'}, ${below ? '0' : '-100%'})`
      }
    });
  };

  return (
    <main className="ping-mesh-page">
      <header className="ping-mesh-page__header">
        <div><h1>Ping Mesh</h1><p>Site-to-site reachability overview</p></div>
        <div className="ping-mesh-page__refresh-control" aria-live="polite">
          <span className="ping-mesh-page__last-refreshed"><small>Last refreshed</small><strong>{formatTimestamp(latestQuery.data?.generatedAt)}</strong></span>
          <div className="ping-mesh-page__auto-refresh" role="group" aria-label="Automatic latest-results refresh">
            <span>Auto refresh</span>
            <div className="ping-mesh-page__auto-refresh-options">
              {AUTO_REFRESH_INTERVALS.map((option) => <button aria-pressed={autoRefresh === option.value} className={autoRefresh === option.value ? 'is-active' : ''} key={option.value} onClick={() => setAutoRefresh(option.value)} type="button">{option.label}</button>)}
            </div>
          </div>
          <button className="ping-mesh-page__refresh-button" disabled={isLoading} onClick={refresh} type="button"><RefreshIcon size={16} />{isLoading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </header>

      {error ? <section className="ping-mesh-page__state ping-mesh-page__state--error" role="alert"><strong>Ping Mesh results could not be loaded.</strong><span>{error.message}</span><button onClick={refresh} type="button">Try again</button></section> : null}
      {isLoading && !latestQuery.data ? <section className="ping-mesh-page__state" aria-live="polite"><strong>Loading Ping Mesh…</strong></section> : null}
      {latestQuery.data && sites.length === 0 ? <section className="ping-mesh-page__state"><strong>No Ping Mesh endpoints.</strong><span>Set endpoints on at least two sites.</span></section> : null}

      {latestQuery.data && sites.length > 0 ? <>
        <section className="ping-mesh-page__kpis" aria-label="Path status summary">
          <KpiCard active={!hasKpiFilter} detail="directed paths" label="Total paths" onClick={clearKpiFilters} value={totalPaths} />
          <KpiCard active={statusFilter === 'healthy'} detail="operating normally" label="Healthy" onClick={() => filterByStatus('healthy')} value={healthyCells} tone="healthy" />
          <KpiCard active={statusFilter === 'degraded'} detail="needs review" label="Degraded" onClick={() => filterByStatus('degraded')} value={degradedCells} tone="degraded" />
          <KpiCard active={statusFilter === 'critical'} detail="action needed" label="Critical" onClick={() => filterByStatus('critical')} value={criticalCells} tone="critical" />
          <KpiCard active={statusFilter === 'down'} detail="unreachable" label="Down" onClick={() => filterByStatus('down')} value={downCells} tone="down" />
          <KpiCard active={freshnessFilter === 'stale'} detail="older probe results" label="Stale" onClick={() => filterByFreshness('stale')} value={staleCells} tone="stale" />
          <KpiCard active={noDataFilter} detail="not probed yet" label="No data" onClick={filterByNoData} value={noDataPaths} tone="empty" />
        </section>
        <section className="ping-mesh-page__filters" aria-label="Matrix filters">
          <SiteMultiSelect label="Source site" onChange={setSourceFilter} sites={sites} value={sourceFilter} />
          <SiteMultiSelect label="Destination site" onChange={setDestinationFilter} sites={sites} value={destinationFilter} />
          {hasSiteFilter ? <button className="ping-mesh-page__clear-site-filters" onClick={clearSiteFilters} type="button"><span aria-hidden="true">×</span> Clear selections</button> : null}
        </section>
        <section className="ping-mesh-page__matrix-card">
        <div className="ping-mesh-page__matrix-heading">
          <div><h2>Directional path heatmap</h2><p>Rows are source sites and columns are destination sites. Each cell is the latest result for that direction.</p></div>
          <div className="ping-mesh-page__matrix-meta">
            <span><strong>{displayedResults.length}</strong> observed paths</span>
            <div className="ping-mesh-page__legend" aria-label="Heatmap legend">
              <span><i className="ping-mesh-page__legend-swatch ping-mesh-page__legend-swatch--good" />Healthy</span>
              <span><i className="ping-mesh-page__legend-swatch ping-mesh-page__legend-swatch--warn" />Degraded</span>
              <span><i className="ping-mesh-page__legend-swatch ping-mesh-page__legend-swatch--critical" />Critical</span>
              <span><i className="ping-mesh-page__legend-swatch ping-mesh-page__legend-swatch--down" />Down</span>
              <span><i className="ping-mesh-page__legend-swatch ping-mesh-page__legend-swatch--stale" />Stale</span>
              <span><i className="ping-mesh-page__legend-swatch ping-mesh-page__legend-swatch--empty" />No data</span>
            </div>
          </div>
        </div>
        <div className="ping-mesh-page__matrix-scroll">
          <div className="ping-mesh-page__matrix" style={{ gridTemplateColumns: `minmax(118px, 1.1fr) repeat(${visibleDestinations.length}, minmax(96px, 1fr))` }}>
            <div className="ping-mesh-page__corner">From (source) ↓&nbsp; / &nbsp;To (destination) →</div>
            {visibleDestinations.map((site) => <SiteHeader key={site.id} site={site} />)}
            {visibleSources.map((source) => <MatrixRow key={source.id} source={source} sites={visibleDestinations} cellByPair={cellByPair} isVisible={matchesMatrixFilters} onHover={showMatrixHover} onLeave={() => setMatrixHover(null)} onSelect={(pair) => { setHistoryLookback('12h'); setSelectedPair(pair); }} selectedPair={selectedPair} />)}
          </div>
        </div>
        {matrixHover ? <MatrixHoverCard {...matrixHover} /> : null}
        </section>
        {selectedPair ? <ProbeHistoryPanel historyCell={selectedHistoryCell} historyError={historyQuery.error} historyLoading={historyQuery.loading} latestCandidate={selectedLatestResult?.candidate} latestCell={selectedLatestCell} lookback={historyLookback} onClose={() => setSelectedPair(null)} onLookback={setHistoryLookback} sites={sites} /> : null}
      </> : null}
    </main>
  );
}

function SiteHeader({ site }: { site: PresentationSite }) {
  return <div className="ping-mesh-page__site-header" title={`${site.name} · ${site.kind}`}><strong>{site.name}</strong></div>;
}

function KpiCard({ label, value, detail, tone, active = false, onClick }: { label: string; value: number; detail?: string; tone?: string; active?: boolean; onClick?: () => void }) {
  const content = <><span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}</>;
  return onClick ? <button aria-pressed={active} className={`ping-mesh-page__kpi ping-mesh-page__kpi--${tone ?? 'neutral'}${active ? ' is-active' : ''}`} onClick={onClick} type="button">{content}</button> : <div className={`ping-mesh-page__kpi ping-mesh-page__kpi--${tone ?? 'neutral'}`}>{content}</div>;
}

function SiteMultiSelect({ label, sites, value, onChange }: { label: string; sites: PresentationSite[]; value: string[]; onChange: (next: string[]) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedSites = sites.filter((site) => value.includes(site.id));
  const toggle = (siteId: string) => {
    onChange(value.includes(siteId) ? value.filter((id) => id !== siteId) : [...value, siteId]);
    setIsOpen(false);
  };
  const remove = (siteId: string) => onChange(value.filter((id) => id !== siteId));
  const selectionLabel = selectedSites.length === 0 ? 'All sites' : selectedSites.length === 1 ? selectedSites[0].name : `${selectedSites.length} sites selected`;
  return <div className="ping-mesh-page__site-filter">
    <span>{label}</span>
    <div className="ping-mesh-page__site-filter-control">
      <details onToggle={(event) => setIsOpen(event.currentTarget.open)} open={isOpen}>
        <summary>{selectionLabel}</summary>
        <div className="ping-mesh-page__site-filter-options">
          {sites.map((site) => <label key={site.id}><input checked={value.includes(site.id)} onChange={() => toggle(site.id)} type="checkbox" /><span>{site.name}</span></label>)}
        </div>
      </details>
      {selectedSites.length > 0 ? <div className="ping-mesh-page__site-filter-chips">{selectedSites.map((site) => <button aria-label={`Remove ${site.name} from ${label}`} key={site.id} onClick={() => remove(site.id)} type="button">{site.name}<span>×</span></button>)}</div> : null}
    </div>
  </div>;
}

function MatrixRow({ source, sites, cellByPair, isVisible, onSelect, selectedPair, onHover, onLeave }: { source: PresentationSite; sites: PresentationSite[]; cellByPair: Map<string, PingMeshCell>; isVisible: (cell?: PingMeshCell) => boolean; onSelect: (pair: string) => void; selectedPair: string | null; onHover: (event: PointerEvent<HTMLElement>, source: PresentationSite, destination: PresentationSite, result?: CandidateResult, unavailable?: boolean) => void; onLeave: () => void }) {
  return <>
    <div className="ping-mesh-page__source-header" title={`${source.name} · ${source.kind}`}><strong>{source.name}</strong></div>
    {sites.map((destination) => source.id === destination.id
      ? <div className="ping-mesh-page__diagonal" key={destination.id} aria-label={`${source.name} to itself`} />
      : <PingMeshResultCell key={destination.id} source={source} destination={destination} cell={cellByPair.get(`${source.id}:${destination.id}`)} isFiltered={!isVisible(cellByPair.get(`${source.id}:${destination.id}`))} isSelected={selectedPair === `${source.id}:${destination.id}`} onHover={onHover} onLeave={onLeave} onSelect={() => onSelect(`${source.id}:${destination.id}`)} />)}
  </>;
}

function PingMeshResultCell({ source, destination, cell, isFiltered, isSelected, onSelect, onHover, onLeave }: { source: PresentationSite; destination: PresentationSite; cell?: PingMeshCell; isFiltered: boolean; isSelected: boolean; onSelect: () => void; onHover: (event: PointerEvent<HTMLElement>, source: PresentationSite, destination: PresentationSite, result?: CandidateResult, unavailable?: boolean) => void; onLeave: () => void }) {
  if (isFiltered) return <div className="ping-mesh-page__cell ping-mesh-page__cell--filtered" aria-label={`${source.name} to ${destination.name}: not included by the active filter`} />;
  if (!cell || cell.candidates.length === 0) return <div className="ping-mesh-page__cell ping-mesh-page__cell--empty" aria-label={`${source.name} to ${destination.name}: no data`} onPointerEnter={(event) => onHover(event, source, destination)} onPointerLeave={onLeave}><span>No data</span></div>;

  const result = primaryResult(cell);
  if (!result || result.packetLoss === undefined) {
    return <div className="ping-mesh-page__cell ping-mesh-page__cell--empty" aria-label={`${source.name} to ${destination.name}: probe result is incomplete`} onPointerEnter={(event) => onHover(event, source, destination, undefined, true)} onPointerLeave={onLeave}><span>Result unavailable</span></div>;
  }

  const status = result.status.toLowerCase();
  return <button className={`ping-mesh-page__cell ping-mesh-page__cell--${status}${result.freshness === 'stale' ? ' is-stale' : ''}${isSelected ? ' is-selected' : ''}`} aria-label={`${source.name} to ${destination.name}: ${result.status}, ${result.freshness}, packet loss ${lossPercent(result.packetLoss)}. Open probe history.`} onClick={onSelect} onPointerEnter={(event) => onHover(event, source, destination, result)} onPointerLeave={onLeave} type="button">
    <div className="ping-mesh-page__cell-status"><span className="ping-mesh-page__status-mark">{status === 'down' ? '×' : status === 'critical' || status === 'degraded' ? '!' : '✓'}</span><strong>{displayStatus(result.status)}</strong><span>{displayStatus(result.freshness)} · {formatAge(result.freshnessAgeSeconds)}</span></div>
    {result.averageRtt !== undefined ? <div className="ping-mesh-page__rtt"><b>Average RTT</b><span>{formatMilliseconds(result.averageRtt)}</span></div> : <span className="ping-mesh-page__rtt-unavailable">RTT not reported</span>}
  </button>;
}

function MatrixHoverCard({ source, destination, result, unavailable = false, style }: MatrixHover) {
  if (!result) return <aside className="ping-mesh-page__matrix-tooltip" role="tooltip" style={style}><strong>{source.name} → {destination.name}</strong><span>{unavailable ? 'Result unavailable.' : 'No probe data for this directed path.'}</span></aside>;
  const rtt = new Map(result.rtt.map((item) => [item.stat, item.value]));
  const rttValues = [
    { label: 'Min', value: rtt.get('min') },
    { label: 'Average', value: rtt.get('avg') },
    { label: 'Max', value: rtt.get('max') },
    { label: 'Deviation', value: rtt.get('mdev') ?? rtt.get('stddev') }
  ].filter((item): item is { label: string; value: number } => item.value !== undefined);
  return <aside className="ping-mesh-page__matrix-tooltip" role="tooltip" style={style}>
    <header><strong>{source.name} → {destination.name}</strong><span className={`ping-mesh-page__tooltip-status ping-mesh-page__tooltip-status--${result.status}`}>{displayStatus(result.status)}</span></header>
    <p className="ping-mesh-page__tooltip-identity"><span>{result.candidate.sourceIp} → {result.candidate.destinationIp}</span></p>
    <div className="ping-mesh-page__tooltip-meta"><span className={`ping-mesh-page__tooltip-freshness ping-mesh-page__tooltip-freshness--${result.freshness}`}>{displayStatus(result.freshness)}</span><span>Last probe {formatAge(result.freshnessAgeSeconds)}</span><span>Loss {lossPercent(result.packetLoss!)}</span></div>
    {rttValues.length > 0 ? <dl className="ping-mesh-page__tooltip-rtt">{rttValues.map(({ label, value }) => <div key={label}><dt>{label}</dt><dd>{formatMilliseconds(value)}</dd></div>)}</dl> : <p className="ping-mesh-page__tooltip-no-rtt">No RTT reported</p>}
    {result.candidateCount && result.candidateCount > 1 ? <em className="ping-mesh-page__tooltip-candidates">Most impacted of {result.candidateCount} endpoint candidates</em> : null}
  </aside>;
}

function ProbeHistoryPanel({ historyCell, latestCell, latestCandidate, historyLoading, historyError, lookback, onLookback, sites, onClose }: {
  historyCell?: PingMeshCell;
  latestCell?: PingMeshCell;
  latestCandidate?: PingMeshCandidate;
  historyLoading: boolean;
  historyError: Error | null;
  lookback: HistoryLookback;
  onLookback: (value: HistoryLookback) => void;
  sites: PresentationSite[];
  onClose: () => void;
}) {
  const cell = historyCell ?? latestCell;
  const sameCandidate = (candidate: PingMeshCandidate) => candidate.sourceDeviceId === latestCandidate?.sourceDeviceId
    && candidate.sourceIp === latestCandidate.sourceIp
    && candidate.destinationIp === latestCandidate.destinationIp;
  // The matrix chose this exact candidate. Never substitute another candidate's
  // history: its latency and counter statistics would contradict the latest
  // status card shown alongside it.
  const candidate = historyCell?.candidates.find(sameCandidate) ?? latestCandidate;
  const history = useMemo(() => candidate ? buildPingMeshProbeHistory(candidate) : [], [candidate]);
  const latest = latestCandidate ? candidateResult(latestCandidate) : undefined;
  const fullViewport = useMemo<Viewport | null>(() => history.length === 0 ? null : {
    start: history[0].observedAt.getTime(),
    end: Math.max(history[history.length - 1].observedAt.getTime(), history[0].observedAt.getTime() + 1)
  }, [history]);
  const [timelineRange, setTimelineRange] = useState<TimelineRange>({ start: 0, end: 100 });
  const [hoveredAt, setHoveredAt] = useState<number | null>(null);
  const [zoomedChart, setZoomedChart] = useState<'rtt' | 'loss' | null>(null);
  const queuedHover = useRef<number | null>(null);
  const hoverFrame = useRef<number | null>(null);
  const source = cell ? sites.find((site) => site.id === cell.sourceSiteId)?.name ?? cell.sourceSiteId : 'Selected source';
  const destination = cell ? sites.find((site) => site.id === cell.destinationSiteId)?.name ?? cell.destinationSiteId : 'Selected destination';
  const activeViewport = useMemo<Viewport | null>(() => {
    if (!fullViewport) return null;
    const fullSpan = fullViewport.end - fullViewport.start;
    return {
      start: fullViewport.start + fullSpan * (timelineRange.start / 100),
      end: fullViewport.start + fullSpan * (timelineRange.end / 100)
    };
  }, [fullViewport, timelineRange]);
  const visiblePoints = useMemo(() => !activeViewport ? [] : history.filter((point) => {
    const time = point.observedAt.getTime();
    return time >= activeViewport.start && time <= activeViewport.end;
  }), [activeViewport, history]);
  const activePoint = useMemo(() => {
    return hoveredAt === null ? undefined : history.find((point) => point.observedAt.getTime() === hoveredAt);
  }, [history, hoveredAt]);

  useEffect(() => {
    setTimelineRange({ start: 0, end: 100 });
    setHoveredAt(null);
    setZoomedChart(null);
  }, [candidate, lookback]);

  useEffect(() => {
    if (!zoomedChart) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setZoomedChart(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [zoomedChart]);

  useEffect(() => () => {
    if (hoverFrame.current !== null) window.cancelAnimationFrame(hoverFrame.current);
  }, []);

  const queueHover = (timestamp: number | null) => {
    queuedHover.current = timestamp;
    if (hoverFrame.current !== null) return;
    hoverFrame.current = window.requestAnimationFrame(() => {
      hoverFrame.current = null;
      setHoveredAt((current) => current === queuedHover.current ? current : queuedHover.current);
    });
  };

  const nearestPoint = (event: PointerEvent<SVGSVGElement>): ProbePoint | undefined => {
    if (!activeViewport || visiblePoints.length === 0) return undefined;
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / Math.max(bounds.width, 1)));
    const time = activeViewport.start + ratio * (activeViewport.end - activeViewport.start);
    return nearestProbe(visiblePoints, time);
  };
  const handlePointerMove = (event: PointerEvent<SVGSVGElement>) => {
    queueHover(nearestPoint(event)?.observedAt.getTime() ?? null);
  };
  const baseline = candidate ? metricRange(candidate.history, RTT_METRIC, 'baseline') : undefined;
  const percentiles = candidate ? rttPercentiles(candidate.history) : undefined;
  return <div aria-label="Close Ping Mesh details" className="ping-mesh-page__history-modal" onMouseDown={onClose} role="presentation">
    <section aria-label={`${source} to ${destination} probe history`} aria-modal="true" className="ping-mesh-page__history" onMouseDown={(event) => event.stopPropagation()} role="dialog">
    <div className="ping-mesh-page__history-heading">
      <div><h3>{source} <span>→</span> {destination}</h3><p>{candidate ? `${candidate.sourceIp} → ${candidate.destinationIp}` : 'Loading selected path'}</p></div>
      <div className="ping-mesh-page__history-toolbar">
        <div className="ping-mesh-page__history-window-context">
          <div className="ping-mesh-page__history-window" role="group" aria-label="Historical window">
            {HISTORY_LOOKBACKS.map((option) => <button aria-pressed={option === lookback} className={option === lookback ? 'is-active' : undefined} key={option} onClick={() => onLookback(option)} type="button">{option}</button>)}
          </div>
          {history.length > 0 ? <span aria-live="polite" className="ping-mesh-page__history-probe-count"><strong>{history.length}</strong><span>probes in selected window</span></span> : null}
        </div>
        <div className="ping-mesh-page__history-actions"><button aria-label="Close probe history" className="ping-mesh-page__history-close" onClick={onClose} type="button">Close</button></div>
      </div>
    </div>
    {historyError ? <p className="ping-mesh-page__history-empty">History could not be loaded: {historyError.message}</p> : null}
    {historyLoading && !historyCell ? <p className="ping-mesh-page__history-empty" aria-live="polite">Loading history…</p> : null}
    {!historyLoading && !historyError && (!candidate || history.length === 0 || !activeViewport) ? <p className="ping-mesh-page__history-empty">No results in this window.</p> : null}
    {candidate && activeViewport && history.length > 0 ? <>
      <div className="ping-mesh-page__history-overview">
        <HistorySummary latest={latest} history={candidate.history} />
        <HistoryDistribution distributions={candidate.history?.distributions ?? []} />
      </div>
      <div className="ping-mesh-page__charts">
        <HistoryChart baseline={baseline} percentiles={percentiles} title="Round-trip time" detail="Selected-window latency" points={visiblePoints} viewport={activeViewport} range={timelineRange} onRangeChange={setTimelineRange} activePoint={activePoint} onPointerLeave={() => queueHover(null)} onPointerMove={handlePointerMove} onZoom={() => setZoomedChart('rtt')} showTimeSlider type="rtt" />
        <HistoryChart title="Packet loss" detail="Loss ratio for each probe" points={visiblePoints} viewport={activeViewport} range={timelineRange} onRangeChange={setTimelineRange} activePoint={activePoint} onPointerLeave={() => queueHover(null)} onPointerMove={handlePointerMove} onZoom={() => setZoomedChart('loss')} type="loss" />
      </div>
    </> : null}
    {zoomedChart && activeViewport ? <div aria-label={`Close ${zoomedChart === 'rtt' ? 'round-trip time' : 'packet loss'} chart zoom`} className="ping-mesh-page__chart-zoom-modal" onMouseDown={() => setZoomedChart(null)} role="presentation">
      <section aria-label={`${zoomedChart === 'rtt' ? 'Round-trip time' : 'Packet loss'} chart enlarged`} aria-modal="true" className="ping-mesh-page__chart-zoom-dialog" onMouseDown={(event) => event.stopPropagation()} role="dialog">
        <header><div><span>Zoomed chart</span><strong>{zoomedChart === 'rtt' ? 'Round-trip time' : 'Packet loss'}</strong></div><button aria-label="Close enlarged chart" onClick={() => setZoomedChart(null)} type="button">Close</button></header>
        {zoomedChart === 'rtt'
          ? <HistoryChart baseline={baseline} expanded percentiles={percentiles} title="Round-trip time" detail="Selected-window latency" points={visiblePoints} viewport={activeViewport} range={timelineRange} onRangeChange={setTimelineRange} activePoint={activePoint} onPointerLeave={() => queueHover(null)} onPointerMove={handlePointerMove} showTimeSlider type="rtt" />
          : <HistoryChart expanded title="Packet loss" detail="Loss ratio for each probe" points={visiblePoints} viewport={activeViewport} range={timelineRange} onRangeChange={setTimelineRange} activePoint={activePoint} onPointerLeave={() => queueHover(null)} onPointerMove={handlePointerMove} showTimeSlider type="loss" />}
      </section>
    </div> : null}
    </section>
  </div>;
}

function metricRange(history: PingMeshHistoryschema | undefined, name: string, stat: string): PingMeshMetricRangeschema | undefined {
  return history?.ranges.find((range) => range.name === name && range.labels.stat === stat);
}

function historyValue(history: PingMeshHistoryschema | undefined, name: string, stat?: string): number | undefined {
  return latestMetricValue(history?.values ?? [], name, stat);
}

function rttPercentiles(history: PingMeshHistoryschema | undefined): RTTPercentiles {
  return {
    p50: historyValue(history, RTT_METRIC, 'p50'),
    p90: historyValue(history, RTT_METRIC, 'p90'),
    p95: historyValue(history, RTT_METRIC, 'p95'),
    p99: historyValue(history, RTT_METRIC, 'p99')
  };
}

function HistorySummary({ latest, history }: { latest?: CandidateResult; history?: PingMeshHistoryschema }) {
  const mean = historyValue(history, RTT_METRIC, 'mean');
  const windowLoss = historyValue(history, PACKET_LOSS_METRIC);
  const availability = historyValue(history, 'ping_mesh_availability_ratio');
  const rttCount = history?.distributions.find((distribution) => distribution.name === RTT_HISTOGRAM_METRIC)?.count;
  const windowLossDetail = windowLoss === undefined
    ? 'Window loss unavailable'
    : windowLoss === 0
      ? 'No loss in selected window'
      : 'Loss ratio across selected window';
  return <div className="ping-mesh-page__history-summary">
    <LatestProbeCard latest={latest} />
    <SummaryCard icon="rtt" label="Average RTT" tone="average" value={mean === undefined ? '—' : formatMilliseconds(mean)} detail={rttCount === undefined ? 'Selected-window mean' : `Mean · ${Math.round(rttCount).toLocaleString()} RTTs`} />
    <SummaryCard icon="loss" label="Packet loss" tone={windowLoss !== undefined && windowLoss > 0 ? 'attention' : undefined} value={windowLoss === undefined ? '—' : lossPercent(windowLoss)} detail={windowLossDetail} />
    <PercentileCard history={history} />
    <SummaryCard icon="availability" label="Availability" value={availability === undefined ? '—' : lossPercent(availability)} detail={availability === undefined ? 'No completion data' : availability >= .99 ? 'Probes completed' : 'Probe completion rate'} />
  </div>;
}

function formatKpiProbeTime(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : formatProbeTime(date);
}

type SummaryIconKind = 'status' | 'rtt' | 'loss' | 'clock' | 'availability';

function SummaryIcon({ kind }: { kind: SummaryIconKind }) {
  if (kind === 'status') return <CheckCircleIcon aria-hidden="true" size={18} />;
  if (kind === 'rtt') return <ActivityIcon aria-hidden="true" size={19} />;
  if (kind === 'loss') return <span aria-hidden="true" className="ping-mesh-page__summary-percent">%</span>;
  if (kind === 'availability') return <CheckCircleIcon aria-hidden="true" size={18} />;
  return <svg aria-hidden="true" fill="none" height="19" viewBox="0 0 24 24" width="19"><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3.3 2" /></svg>;
}

function SummaryCard({ icon, label, value, detail, tone }: { icon: SummaryIconKind; label: string; value: string; detail?: string; tone?: string }) {
  return <article className={`ping-mesh-page__summary-card${tone ? ` ping-mesh-page__summary-card--${tone}` : ''}`}>
    <span className="ping-mesh-page__summary-icon"><SummaryIcon kind={icon} /></span>
    <div className="ping-mesh-page__summary-card-content"><span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}</div>
  </article>;
}

function PercentileCard({ history }: { history?: PingMeshHistoryschema }) {
  const values = [
    { stat: 'p50', label: 'P50', summary: '50% at or below' },
    { stat: 'p90', label: 'P90', summary: '90% at or below' },
    { stat: 'p95', label: 'P95', summary: '95% at or below' },
    { stat: 'p99', label: 'P99', summary: '99% at or below' },
  ].map((percentile) => ({ ...percentile, value: historyValue(history, RTT_METRIC, percentile.stat) }));
  return <article className="ping-mesh-page__summary-card ping-mesh-page__summary-card--percentiles">
    <span className="ping-mesh-page__summary-icon"><ChartIcon aria-hidden="true" size={18} /></span>
    <div className="ping-mesh-page__summary-percentile-heading"><span>RTT percentiles</span><small>RTT distribution across responses; higher percentiles reveal tail latency and spikes.</small></div>
    <dl>{values.map(({ label, value, summary }) => <div key={label}><dt>{label}</dt><dd>{value === undefined ? '—' : formatMilliseconds(value)}</dd><small>{summary}</small></div>)}</dl>
  </article>;
}

function LatestProbeCard({ latest }: { latest?: CandidateResult }) {
  const latestRTT = (stat: string) => latest?.rtt.find((metric) => metric.stat === stat)?.value;
  const rtt = [
    { label: 'Min', value: latestRTT('min') },
    { label: 'Avg', value: latestRTT('avg') },
    { label: 'Max', value: latestRTT('max') }
  ];
  const stale = latest?.freshness === 'stale';
  const detail = latest ? `${formatKpiProbeTime(latest.latest.observedAt)} · Loss ${latest.packetLoss === undefined ? '—' : lossPercent(latest.packetLoss)}${stale ? ' · Stale' : ''}` : 'Latest probe unavailable';
  return <article className={`ping-mesh-page__summary-card ping-mesh-page__summary-card--last-probe${latest ? ` ping-mesh-page__summary-card--${latest.status}` : ''}`}>
    <span className="ping-mesh-page__summary-icon"><SummaryIcon kind="status" /></span>
    <div className="ping-mesh-page__summary-card-content"><span>Current probe</span><strong>{latest ? displayStatus(latest.status) : '—'}</strong><small>{detail}</small></div>
    <dl aria-label="Current probe RTT details">{rtt.map(({ label, value }) => <div key={label}><dt>{label}</dt><dd>{value === undefined ? '—' : formatMilliseconds(value)}</dd></div>)}</dl>
  </article>;
}

function HistoryTimeSlider({ label, viewport, range, onChange }: { label: string; viewport: Viewport; range: TimelineRange; onChange: (range: TimelineRange) => void }) {
  const startLabel = formatTimelineTick(new Date(viewport.start), true, false);
  const endLabel = formatTimelineTick(new Date(viewport.end), false, true);
  const drag = useRef<{ mode: 'start' | 'end' | 'pan' | 'select'; origin: number; range: TimelineRange; pointerId: number; moved: boolean } | null>(null);
  const minSpan = 8;
  const positionAt = (event: PointerEvent<HTMLElement>): number => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return Math.min(100, Math.max(0, ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * 100));
  };
  const clampRange = (next: TimelineRange): TimelineRange => {
    const span = Math.max(minSpan, Math.min(100, next.end - next.start));
    const start = Math.min(Math.max(next.start, 0), 100 - span);
    return { start, end: start + span };
  };
  const handlePointerDown = (event: PointerEvent<HTMLElement>) => {
    const position = positionAt(event);
    const handleZone = 3;
    const hasFullSelection = range.end - range.start >= 99;
    const mode = Math.abs(position - range.start) <= handleZone ? 'start'
      : Math.abs(position - range.end) <= handleZone ? 'end'
        : position >= range.start && position <= range.end && !hasFullSelection ? 'pan'
          : 'select';
    drag.current = { mode, origin: position, range, pointerId: event.pointerId, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const position = positionAt(event);
    // A simple click must not create a tiny range. Start changing the
    // selection only after an intentional drag, which also prevents the
    // navigator from feeling attached to the pointer after a click.
    if (!state.moved && Math.abs(position - state.origin) < .7) return;
    state.moved = true;
    if (state.mode === 'start') onChange(clampRange({ start: Math.min(position, state.range.end - minSpan), end: state.range.end }));
    else if (state.mode === 'end') onChange(clampRange({ start: state.range.start, end: Math.max(position, state.range.start + minSpan) }));
    else if (state.mode === 'select') onChange(clampRange({ start: Math.min(state.origin, position), end: Math.max(state.origin, position) }));
    else {
      const delta = position - state.origin;
      onChange(clampRange({ start: state.range.start + delta, end: state.range.end + delta }));
    }
  };
  const stopDrag = (event: PointerEvent<HTMLElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const cancelDrag = () => { drag.current = null; };
  return <section className="ping-mesh-page__history-time-slider" aria-label={`${label} time range`}>
    <div className="ping-mesh-page__history-navigator-track" onLostPointerCapture={cancelDrag} onPointerCancel={stopDrag} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={stopDrag} role="group" aria-label={`${label} time range slider`}>
      <span aria-hidden="true" className="ping-mesh-page__history-navigator-selection" style={{ left: `${range.start}%`, width: `${range.end - range.start}%` }}><i className="ping-mesh-page__history-navigator-handle ping-mesh-page__history-navigator-handle--start" /><i className="ping-mesh-page__history-navigator-handle ping-mesh-page__history-navigator-handle--end" /></span>
    </div>
    <div className="ping-mesh-page__history-zoom-labels"><span>{startLabel}</span><span>{endLabel}</span></div>
  </section>;
}

function HistoryDistribution({ distributions }: { distributions: PingMeshDistributionschema[] }) {
  const distribution = distributions.find((item) => item.name === RTT_HISTOGRAM_METRIC);
  if (!distribution || distribution.count <= 0) return null;
  const max = Math.max(...distribution.buckets.map((bucket) => bucket.count), 1);
  const formatCount = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: value < 10 ? 1 : 0 });
  const formatPercentage = (value: number) => `${value.toFixed(value === 0 || value >= 10 ? 0 : 1)}%`;
  return <section className="ping-mesh-page__distribution"><header><div><h4>RTT distribution</h4><span>Packet RTTs by latency range</span></div><p>{Math.round(distribution.count)} packet RTTs · selected window</p></header><div className="ping-mesh-page__distribution-body"><div className="ping-mesh-page__distribution-plot"><div aria-hidden="true" className="ping-mesh-page__distribution-y-axis"><b>Count</b><div><span>{formatCount(max)}</span><span>{formatCount(max / 2)}</span><span>0</span></div></div><div className="ping-mesh-page__distribution-bars" aria-label="RTT histogram distribution">{distribution.buckets.map((bucket) => {
    const height = Math.max(5, (bucket.count / max) * 100);
    return <div key={bucket.label} title={`${bucket.label}: ${bucket.count.toLocaleString(undefined, { maximumFractionDigits: 1 })} packet RTTs (${bucket.percentage.toFixed(1)}%)`}><span className="ping-mesh-page__distribution-bar-column"><span className={`ping-mesh-page__distribution-bar-track${bucket.count === 0 ? ' is-empty' : ''}`} style={{ '--bucket-height': `${height}%` } as CSSProperties}><i /><span className="ping-mesh-page__distribution-bar-value"><strong>{formatCount(bucket.count)}</strong><small>{formatPercentage(bucket.percentage)}</small></span></span></span><span>{bucket.label}</span></div>;
  })}</div></div></div></section>;
}

function continuousRuns(points: ProbePoint[], predicate: (point: ProbePoint) => boolean): ProbePoint[][] {
  const runs: ProbePoint[][] = [];
  let activeRun: ProbePoint[] | null = null;
  for (const point of points) {
    if (!predicate(point)) {
      activeRun = null;
      continue;
    }
    if (!activeRun) {
      activeRun = [];
      runs.push(activeRun);
    }
    activeRun.push(point);
  }
  return runs;
}

function timelineTicks(viewport: Viewport): Date[] {
  const span = viewport.end - viewport.start;
  // Pick human time boundaries instead of arbitrary fractions of the plot. This
  // makes short windows read in minutes and longer ones read in hours/days.
  const intervals = [60_000, 2 * 60_000, 5 * 60_000, 10 * 60_000, 15 * 60_000, 30 * 60_000, 60 * 60_000, 2 * 60 * 60_000, 3 * 60 * 60_000, 6 * 60 * 60_000, 12 * 60 * 60_000, 24 * 60 * 60_000];
  const step = intervals.find((interval) => span / interval <= 4) ?? intervals[intervals.length - 1];
  const ticks: Date[] = [new Date(viewport.start)];
  // The edge labels include both the date and time. Skip an interior boundary
  // that falls too close to either edge so those labels remain legible in the
  // compact, side-by-side charts.
  const edgePadding = span * 0.18;
  for (let time = Math.ceil(viewport.start / step) * step; time < viewport.end; time += step) {
    if (time - viewport.start >= edgePadding && viewport.end - time >= edgePadding) ticks.push(new Date(time));
  }
  ticks.push(new Date(viewport.end));
  return ticks.filter((tick, index, all) => index === 0 || tick.getTime() !== all[index - 1].getTime());
}

function formatTimelineTick(value: Date, first: boolean, last: boolean): string {
  if (first || last) {
    return value.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatChartPeakTime(value: Date): string {
  return value.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function HistoryChart({ title, detail, points, viewport, range, onRangeChange, activePoint, onPointerLeave, onPointerMove, type, baseline, percentiles, showTimeSlider = false, onZoom, expanded = false }: {
  title: string; detail: string; points: ProbePoint[]; viewport: Viewport; range: TimelineRange; onRangeChange: (range: TimelineRange) => void; activePoint?: ProbePoint; onPointerLeave: () => void; onPointerMove: (event: PointerEvent<SVGSVGElement>) => void; type: 'rtt' | 'loss'; baseline?: PingMeshMetricRangeschema; percentiles?: RTTPercentiles; showTimeSlider?: boolean; onZoom?: () => void; expanded?: boolean;
}) {
  const width = 1000;
  // Keep the viewBox ratio aligned with the rendered chart. Text and tick
  // labels then scale uniformly instead of being horizontally stretched.
  const height = 400;
  // Keep a compact y-axis gutter: labels have clear space without shrinking
  // the graph's useful plotting area.
  const left = 78;
  const right = 18;
  const top = 34;
  const bottom = 76;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const rttPoints = points.filter((point) => point.avg !== undefined);
  const lossPoints = points.filter((point) => point.packetLoss !== undefined);
  const peakLoss = type === 'loss' && lossPoints.length > 0
    ? lossPoints.reduce((peak, point) => point.packetLoss! > peak.packetLoss! ? point : peak)
    : undefined;
  const x = (point: ProbePoint) => left + ((point.observedAt.getTime() - viewport.start) / Math.max(viewport.end - viewport.start, 1)) * plotWidth;
  const rttValues = rttPoints.flatMap((point) => [point.min, point.avg, point.max].filter((value): value is number => value !== undefined).map((value) => value * 1000));
  const percentileValues = type === 'rtt' ? Object.values(percentiles ?? {}).filter((value): value is number => value !== undefined).map((value) => value * 1000) : [];
  const rttScaleValues = [...rttValues, ...percentileValues];
  const low = type === 'loss' ? 0 : (rttScaleValues.length > 0 ? Math.min(...rttScaleValues) : 0);
  const high = type === 'loss' ? 100 : Math.max(...rttScaleValues, low + 1);
  const padding = type === 'loss' ? 0 : Math.max((high - low) * 0.12, 1);
  const minValue = type === 'loss' ? 0 : Math.max(0, low - padding);
  const maxValue = type === 'loss' ? 100 : high + padding;
  const y = (value: number) => top + (1 - ((value - minValue) / Math.max(maxValue - minValue, 1))) * plotHeight;
  const p50Baseline = type === 'rtt' ? percentiles?.p50 ?? baseline?.lower : undefined;
  const p90Baseline = type === 'rtt' ? percentiles?.p90 ?? baseline?.upper : undefined;
  const p50Visible = p50Baseline !== undefined && p50Baseline * 1000 >= minValue && p50Baseline * 1000 <= maxValue;
  const p90Visible = p90Baseline !== undefined && p90Baseline * 1000 >= minValue && p90Baseline * 1000 <= maxValue;
  const pointsFor = (run: ProbePoint[], field: 'avg' | 'min' | 'max') => run.filter((point) => point[field] !== undefined).map((point) => `${x(point).toFixed(1)},${y(point[field]! * 1000).toFixed(1)}`).join(' ');
  const rttRuns = continuousRuns(points, (point) => point.avg !== undefined);
  const bandRuns = continuousRuns(points, (point) => point.min !== undefined && point.max !== undefined);
  const lossRuns = continuousRuns(points, (point) => point.packetLoss !== undefined);
  const lossPointsFor = (run: ProbePoint[]) => run.map((point) => `${x(point).toFixed(1)},${y(point.packetLoss! * 100).toFixed(1)}`).join(' ');
  const activeVisible = activePoint && activePoint.observedAt.getTime() >= viewport.start && activePoint.observedAt.getTime() <= viewport.end ? activePoint : undefined;
  const ticks = timelineTicks(viewport);
  const chartPoint = activeVisible && (type === 'rtt' ? activeVisible.avg !== undefined : activeVisible.packetLoss !== undefined) ? activeVisible : undefined;
  const chartPointX = chartPoint ? x(chartPoint) : undefined;
  const tooltipXRatio = chartPointX === undefined ? 0.5 : chartPointX / width;
  const tooltipTransform = tooltipXRatio < 0.22 ? 'translateX(0)' : tooltipXRatio > 0.78 ? 'translateX(-100%)' : 'translateX(-50%)';
  // Keep detail in a stable top lane. Only its horizontal position follows the
  // shared probe timestamp; the matching timestamp badge stays on the x-axis.
  const chartTooltipStyle: CSSProperties = chartPointX === undefined ? {} : {
    left: `${tooltipXRatio * 100}%`,
    top: type === 'rtt' ? '82px' : '52px',
    transform: tooltipTransform,
  };
  const timestampLabel = chartPoint ? formatProbeTime(chartPoint.observedAt) : undefined;
  const timestampWidth = timestampLabel ? Math.min(360, timestampLabel.length * 12 + 16) : 0;
  const timestampX = chartPointX === undefined ? 0 : Math.min(width - right - timestampWidth, Math.max(left, chartPointX - timestampWidth / 2));

  return <section className={`ping-mesh-page__chart ping-mesh-page__chart--${type}${expanded ? ' is-expanded' : ''}`}>
    <div className="ping-mesh-page__chart-heading"><div><h4>{title}</h4><span>{detail}</span></div><div className="ping-mesh-page__chart-header-meta">{type === 'loss' ? peakLoss && peakLoss.packetLoss! > 0 ? <span className="ping-mesh-page__chart-peak"><b>Peak loss</b><strong>{lossPercent(peakLoss.packetLoss!)}</strong><small>at {formatChartPeakTime(peakLoss.observedAt)}</small></span> : <span className="ping-mesh-page__chart-no-peak">No loss observed</span> : null}<span className="ping-mesh-page__chart-unit">{type === 'loss' ? '0–100%' : 'milliseconds'}</span>{onZoom ? <button aria-label={`Expand ${title} chart`} className="ping-mesh-page__chart-zoom-button" onClick={onZoom} title={`Expand ${title} chart`} type="button"><ExpandIcon aria-hidden="true" size={14} /><span>Zoom</span></button> : null}</div></div>
    {type === 'rtt' ? <div className="ping-mesh-page__chart-legend" aria-label="Round-trip time chart legend"><span><i className="ping-mesh-page__chart-legend-average" />Average</span><span><i className="ping-mesh-page__chart-legend-range" />Min–max range</span><span><i className="ping-mesh-page__chart-legend-baseline ping-mesh-page__chart-legend-baseline--p50" />P50 baseline</span><span><i className="ping-mesh-page__chart-legend-baseline ping-mesh-page__chart-legend-baseline--p90" />P90 baseline</span></div> : null}
    {chartPoint ? <aside className="ping-mesh-page__chart-tooltip" style={chartTooltipStyle}>
      <strong>{chartTooltipTitle(type)}</strong>
      <span>{chartProbePointDetails(chartPoint, type)}</span>
    </aside> : null}
    {(type === 'rtt' ? rttPoints : lossPoints).length === 0 ? <p className="ping-mesh-page__chart-empty">No {type === 'loss' ? 'packet-loss' : 'RTT'} samples were returned for this range.</p> : <svg aria-label={`${title} history chart`} onPointerLeave={onPointerLeave} onPointerMove={onPointerMove} preserveAspectRatio="xMidYMid meet" role="img" viewBox={`0 0 ${width} ${height}`}>
      {[0, 0.5, 1].map((ratio) => <line className="ping-mesh-page__chart-grid" key={ratio} x1={left} x2={width - right} y1={y(minValue + (maxValue - minValue) * ratio)} y2={y(minValue + (maxValue - minValue) * ratio)} />)}
      {ticks.map((tick) => <line className="ping-mesh-page__chart-grid ping-mesh-page__chart-grid--vertical" key={tick.getTime()} x1={left + ((tick.getTime() - viewport.start) / Math.max(viewport.end - viewport.start, 1)) * plotWidth} x2={left + ((tick.getTime() - viewport.start) / Math.max(viewport.end - viewport.start, 1)) * plotWidth} y1={top} y2={height - bottom} />)}
      <line className="ping-mesh-page__chart-axis" x1={left} x2={width - right} y1={height - bottom} y2={height - bottom} />
      <line className="ping-mesh-page__chart-axis" x1={left} x2={left} y1={top} y2={height - bottom} />
      <text className="ping-mesh-page__chart-label" textAnchor="end" x={left - 10} y={top + 4}>{type === 'loss' ? '100%' : `${maxValue.toFixed(1)} ms`}</text>
      <text className="ping-mesh-page__chart-label" textAnchor="end" x={left - 10} y={y(minValue + (maxValue - minValue) * 0.5) + 5}>{type === 'loss' ? '50%' : `${(minValue + (maxValue - minValue) * 0.5).toFixed(1)} ms`}</text>
      <text className="ping-mesh-page__chart-label" textAnchor="end" x={left - 10} y={height - bottom + 4}>{type === 'loss' ? '0%' : `${minValue.toFixed(1)} ms`}</text>
      {type === 'rtt' ? <>
        {p50Visible && p50Baseline !== undefined ? <line className="ping-mesh-page__chart-baseline-line ping-mesh-page__chart-baseline-line--lower" x1={left} x2={width - right} y1={y(p50Baseline * 1000)} y2={y(p50Baseline * 1000)} /> : null}
        {p90Visible && p90Baseline !== undefined ? <line className="ping-mesh-page__chart-baseline-line ping-mesh-page__chart-baseline-line--upper" x1={left} x2={width - right} y1={y(p90Baseline * 1000)} y2={y(p90Baseline * 1000)} /> : null}
        {bandRuns.map((run) => <polygon className="ping-mesh-page__chart-band" key={`band-${dateKey(run[0].observedAt)}`} points={`${run.map((point) => `${x(point).toFixed(1)},${y(point.max! * 1000).toFixed(1)}`).join(' ')} ${[...run].reverse().map((point) => `${x(point).toFixed(1)},${y(point.min! * 1000).toFixed(1)}`).join(' ')}`} />)}
        {bandRuns.map((run) => <polyline className="ping-mesh-page__chart-max-line" fill="none" key={`max-${dateKey(run[0].observedAt)}`} points={pointsFor(run, 'max')} />)}
        {rttRuns.map((run) => <polyline className="ping-mesh-page__chart-line ping-mesh-page__chart-line--rtt" fill="none" key={`line-${dateKey(run[0].observedAt)}`} points={pointsFor(run, 'avg')} />)}
      </> : <>{lossRuns.map((run) => <polyline className="ping-mesh-page__chart-line ping-mesh-page__chart-line--loss" fill="none" key={`loss-${dateKey(run[0].observedAt)}`} points={lossPointsFor(run)} />)}</>}
      {activeVisible ? <>
        <line className="ping-mesh-page__chart-crosshair" x1={x(activeVisible)} x2={x(activeVisible)} y1={top} y2={height - bottom} />
        {type === 'rtt' && activeVisible.avg !== undefined ? <circle className="ping-mesh-page__chart-active-point" cx={x(activeVisible)} cy={y(activeVisible.avg * 1000)} r="5" /> : null}
        {type === 'loss' && activeVisible.packetLoss !== undefined ? <circle className="ping-mesh-page__chart-active-point ping-mesh-page__chart-active-point--loss" cx={x(activeVisible)} cy={y(activeVisible.packetLoss * 100)} r="5" /> : null}
      </> : null}
      {ticks.map((tick, index) => <text className="ping-mesh-page__chart-label ping-mesh-page__chart-label--time" key={tick.getTime()} textAnchor={index === 0 ? 'start' : index === ticks.length - 1 ? 'end' : 'middle'} x={left + ((tick.getTime() - viewport.start) / Math.max(viewport.end - viewport.start, 1)) * plotWidth} y={height - 13}>{formatTimelineTick(tick, index === 0, index === ticks.length - 1)}</text>)}
      {timestampLabel ? <g className="ping-mesh-page__chart-timestamp"><rect height="42" width={timestampWidth} x={timestampX} y={height - bottom + 8} /><text x={timestampX + timestampWidth / 2} y={height - bottom + 34} textAnchor="middle">{timestampLabel}</text></g> : null}
      <rect className="ping-mesh-page__chart-hit" height={plotHeight} width={plotWidth} x={left} y={top} />
    </svg>}
    {showTimeSlider ? <HistoryTimeSlider label={title} onChange={onRangeChange} range={range} viewport={viewport} /> : null}
  </section>;
}

function chartTooltipTitle(type: 'rtt' | 'loss'): string {
  return type === 'rtt' ? 'Round-trip time' : 'Packet loss';
}

function chartProbePointDetails(point: ProbePoint, type: 'rtt' | 'loss'): string {
  const values = type === 'loss'
    ? [point.packetLoss === undefined ? undefined : `Packet loss ${lossPercent(point.packetLoss)}`]
    : [
      point.min === undefined ? undefined : `Min ${formatMilliseconds(point.min)}`,
      point.avg === undefined ? undefined : `Average ${formatMilliseconds(point.avg)}`,
      point.max === undefined ? undefined : `Max ${formatMilliseconds(point.max)}`,
      point.deviation === undefined ? undefined : `Deviation ${formatMilliseconds(point.deviation)}`,
      ...point.maxFlags.map((flag) => flag === 'above_p95'
        ? 'Max RTT is above the window P95.'
        : `Max RTT flag: ${flag}`)
    ];
  return values.filter((value): value is string => Boolean(value)).join('\n');
}
